"use client";

import { useRef, type ComponentType, type ReactNode, type SVGProps } from "react";
import { gsap, PLAY_ONCE, useGSAP } from "@/lib/gsap";
import { blurReveal, fadeUp, reduceMotion } from "@/lib/motion";
import { AnimatedCard } from "@/components/motion/AnimatedCard";
import { TextReveal } from "@/components/motion/TextReveal";
import { Button } from "@/components/ui/Button";
import { ChartBars, ChatBubble, CheckSquare, Megaphone, Target, User } from "@/components/ui/icons";
import { PRODUCTS, SUITE, productByKey } from "@/content/quikit";
import { productHref } from "@/components/layout/nav-data";
import { cn } from "@/lib/utils";
import { SectionIntro } from "./SectionIntro";

type Icon = ComponentType<SVGProps<SVGSVGElement>>;
const ICONS: Record<string, Icon> = {
  chart: ChartBars,
  check: CheckSquare,
  user: User,
  megaphone: Megaphone,
  chat: ChatBubble,
  target: Target,
};

/* ------------------------------------------------------------------ */
/*  Tile                                                               */
/* ------------------------------------------------------------------ */
type TileProps = {
  index: number;
  title: string;
  text: string;
  apps: readonly string[];
  icon: string;
  wide?: boolean;
  children?: ReactNode;
};

/**
 * A suite tile. It rises with its row, then runs its own sequence: title words de-blur,
 * copy settles, app chips pop in and the mini-visual grows (`[data-bento-grow]`).
 */
function Tile({ index, title, text, apps, icon, wide, children }: TileProps) {
  const inner = useRef<HTMLDivElement>(null);
  const Glyph = ICONS[icon];

  useGSAP(
    () => {
      const el = inner.current;
      if (!el || reduceMotion()) return;
      const q = (s: string) => gsap.utils.toArray<HTMLElement>(s, el);
      const tl = gsap
        .timeline({ scrollTrigger: { trigger: el, start: "top 82%", toggleActions: PLAY_ONCE }, delay: 0.08 + (index % 3) * 0.08 })
        .fromTo(q("[data-tile-icon]"), { opacity: 0, scale: 0.6 }, { opacity: 1, scale: 1, duration: 0.6, ease: "back.out(1.7)" }, 0)
        .add(blurReveal(q("[data-tr-unit]"), { stagger: 0.07 }), 0.08)
        .add(fadeUp(q("[data-tile-text]")), 0.3)
        .fromTo(
          q("[data-tile-chip]"),
          { opacity: 0, scale: 0.86, y: 8 },
          { opacity: 1, scale: 1, y: 0, duration: 0.6, ease: "back.out(1.5)", stagger: 0.06 },
          0.45,
        );

      // Only some tiles carry a mini-visual. An empty selection makes GSAP warn "target not
      // found", so the visual's steps are added only where it is rendered.
      const grow = q("[data-bento-grow]");
      if (grow.length) {
        tl.fromTo(grow, { scaleX: 0, transformOrigin: "0% 50%" }, { scaleX: 1, duration: 1.4, ease: "silk", stagger: 0.1 }, 0.5);
      }
      const items = q("[data-bento-item]");
      if (items.length) {
        tl.fromTo(items, { opacity: 0, y: 14 }, { opacity: 1, y: 0, duration: 0.8, ease: "silk", stagger: 0.07 }, 0.45);
      }
    },
    { scope: inner },
  );

  return (
    <AnimatedCard
      index={index % 3}
      y={70}
      stagger={0.1}
      className={cn("relative overflow-hidden rounded-[24px] bg-panel", wide && "md:col-span-2")}
    >
      <div ref={inner} className={cn("flex h-full flex-col gap-8 p-6 md:p-7", wide && "md:flex-row md:items-stretch")}>
        <div className={cn("flex flex-col", wide && "md:w-[32%]")}>
          <span
            data-tile-icon=""
            data-reveal-init=""
            className="grid size-10 place-items-center rounded-[12px] bg-white text-ink shadow-[0_1px_2px_rgba(0,0,0,0.06)]"
          >
            <Glyph className="size-[18px]" />
          </span>
          <TextReveal
            as="h3"
            mode="manual"
            text={title}
            className="mt-5 text-[clamp(1.35rem,1.8vw,1.6rem)] font-[450] leading-[1.18] tracking-[-0.03em]"
          />
          <p data-tile-text="" data-reveal-init="" className="mt-3 max-w-[36ch] text-[14px] leading-[1.6] text-muted">
            {text}
          </p>
          <div className="mt-auto flex flex-wrap gap-1.5 pt-6">
            {apps.map((key) => (
              <a
                key={key}
                href={productHref(key)}
                data-tile-chip=""
                data-reveal-init=""
                className="rounded-full bg-white px-3 py-1.5 text-[12px] font-medium shadow-[0_1px_2px_rgba(0,0,0,0.05)] transition-colors hover:bg-ink hover:text-white"
              >
                {productByKey(key).name}
              </a>
            ))}
          </div>
        </div>
        {children && <div className={cn("relative", wide && "md:flex-1")}>{children}</div>}
      </div>
    </AnimatedCard>
  );
}

/* ------------------------------------------------------------------ */
/*  Mini visuals                                                       */
/* ------------------------------------------------------------------ */
const white = "rounded-[16px] bg-white shadow-[0_1px_2px_rgba(0,0,0,0.04)]";

function Pipeline() {
  const cols = [
    { stage: "Qualified", deals: [["Acme Retail", "$4,200"], ["Northwind", "$2,800"]] },
    { stage: "Proposal", deals: [["Globex Ltd", "$6,500"]] },
    { stage: "Won", deals: [["Initech", "$3,100"]] },
  ];
  return (
    <div className="grid h-full grid-cols-3 gap-2">
      {cols.map((c) => (
        <div key={c.stage} data-bento-item="" data-reveal-init="" className="rounded-[16px] bg-white/60 p-2.5">
          <p className="flex items-center justify-between px-1 text-[11px] font-medium text-muted">
            {c.stage} <span>{c.deals.length}</span>
          </p>
          <div className="mt-2 space-y-2">
            {c.deals.map(([name, value]) => (
              <div key={name} className={cn(white, "p-2.5")}>
                <p className="text-[11.5px] font-medium">{name}</p>
                <p className="mt-0.5 text-[11px] text-muted">{value}</p>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function Channels() {
  const rows = [
    ["LinkedIn", "38%", "bg-[#5fd9cc]"],
    ["Google Search", "31%", "bg-ink"],
    ["Email", "19%", "bg-[#a78bfa]"],
    ["Referral", "12%", "bg-[#fcd34d]"],
  ];
  return (
    <div data-bento-item="" data-reveal-init="" className={cn(white, "flex h-full flex-col justify-center gap-4 p-5")}>
      <p className="text-[12px] font-medium">Pipeline by channel</p>
      {rows.map(([label, share, tone]) => (
        <div key={label}>
          <div className="flex justify-between text-[11.5px]">
            <span>{label}</span>
            <span className="text-muted">{share}</span>
          </div>
          <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-panel">
            <div data-bento-grow="" className={cn("h-full rounded-full", tone)} style={{ width: share }} />
          </div>
        </div>
      ))}
    </div>
  );
}

function Okrs() {
  const rows = [
    ["Grow ARR to $1.2M", 72],
    ["Launch QuikSales in 3 markets", 48],
    ["Cut onboarding time by 30%", 86],
  ] as const;
  return (
    <div className="flex h-full flex-col justify-center gap-2">
      {rows.map(([label, pct]) => (
        <div key={label} data-bento-item="" data-reveal-init="" className={cn(white, "p-3.5")}>
          <div className="flex items-center justify-between gap-3 text-[12px]">
            <span className="font-medium">{label}</span>
            <span className="tabular-nums text-muted">{pct}%</span>
          </div>
          <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-panel">
            <div
              data-bento-grow=""
              className={cn("h-full rounded-full", pct < 50 ? "bg-[#f0a54a]" : "bg-[#5fd9cc]")}
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

/** Wide tiles (with a mini-visual), keyed by card title so the grid order can change freely. */
const VISUALS: Record<string, ReactNode> = {
  "Grow revenue": <Pipeline />,
  "Market smarter": <Channels />,
  "Align strategy": <Okrs />,
};

/* ------------------------------------------------------------------ */
/*  Section                                                            */
/* ------------------------------------------------------------------ */
export function FeatureGrid() {
  return (
    <section id="suite" aria-labelledby="suite-title" className="pt-[clamp(120px,12vw,190px)]">
      <div className="wrap">
        <SectionIntro eyebrow={SUITE.eyebrow} title={SUITE.title} titleId="suite-title" text={SUITE.text} textClassName="max-w-[48ch]" />
        <div className="mt-12 grid gap-3 md:mt-14 md:grid-cols-3 md:gap-4">
          {SUITE.cards.map((card, i) => (
            <Tile key={card.title} index={i} wide={card.title in VISUALS} {...card}>
              {VISUALS[card.title]}
            </Tile>
          ))}
        </div>
        <div className="mt-10 flex justify-center">
          <Button variant="outline" href="/products" arrow="inline">
            {`Explore all ${PRODUCTS.length} apps`}
          </Button>
        </div>
      </div>
    </section>
  );
}
